<template>
  <transition name="fromBottom">
    <div
      v-if="compareList.length && route.name !== 'lang-compare'"
      class="StickyBottomCompare fixed bottom-0 w-full z-underHeader bg-bg-base border-t border-border-base"
    >
      <!-- Mobile: sidescroll -->
      <div class="desk:hidden flex items-center py-12 px-16 gap-12">
        <div class="shrink-0">
          <div class="type-tagline whitespace-nowrap">{{ $t('compare.sticky.heading') }}</div>
          <div class="type-sm text-text-subtle">{{ $t('compare.sticky.selected', { count: compareList.length, max: MAX_COMPARE }) }}</div>
        </div>
        <SideScroll fade-out class="basis-full min-w-0">
          <div class="flex gap-8">
            <div
              v-for="product in compareList"
              :key="product.partNo"
              class="border border-border-base bg-bg-subtle flex items-center gap-4 shrink-0"
            >
              <nuxt-img
                v-if="product.imageUrl"
                :src="product.imageUrl"
                provider="norce"
                preset="plpimage"
                sizes="mob375:56px"
                class="w-56 h-56 object-cover shrink-0"
                :alt="product.name"
              />
              <div v-else class="w-56 h-56 bg-bg-base flex items-center justify-center shrink-0">
                <img
                  src="/icons/no-image.svg"
                  class="w-24 h-24"
                  alt=""
                >
              </div>
              <div class="px-4 min-w-0">
                <div class="type-headline-sm truncate">{{ product.name }}</div>
              </div>
              <button
                :title="$t('compare.button.remove')"
                class="px-8 py-4 shrink-0"
                @click="removeFromCompare(product.partNo)"
              >
                <img
                  src="/icons/close.svg"
                  class="w-12 h-12"
                  alt=""
                >
              </button>
            </div>
          </div>
        </SideScroll>
        <nuxt-link :to="compareUrl" class="btn shrink-0">
          {{ $t('compare.sticky.button', { count: compareList.length }) }}
        </nuxt-link>
      </div>

      <!-- Desktop: 3 fixed slots -->
      <div class="hidden desk:flex items-center py-8 px-24 gap-24">
        <!-- Left: icon + heading -->
        <div class="flex items-center gap-12 shrink-0">
          <img
            src="/icons/move-left-right.svg"
            class="w-20 h-20 shrink-0"
            alt=""
          >
          <div>
            <div class="type-tagline whitespace-nowrap">{{ $t('compare.sticky.heading') }}</div>
            <div class="type-sm text-text-subtle whitespace-nowrap">
              {{ $t('compare.sticky.selected', { count: compareList.length, max: MAX_COMPARE }) }}
            </div>
          </div>
        </div>

        <!-- Middle: 3 fixed slots -->
        <div class="flex flex-1 gap-8 min-w-0">
          <div
            v-for="i in MAX_COMPARE"
            :key="i"
            class="flex-1 min-w-0"
          >
            <div
              v-if="compareList[i - 1]"
              class="flex items-center gap-4 p-4
              border border-dashed bg-bg-subtle border-border-base h-56 overflow-hidden"
            >
              <nuxt-img
                v-if="compareList[i - 1].imageUrl"
                :src="compareList[i - 1].imageUrl"
                provider="norce"
                preset="plpimage"
                sizes="desk:48px"
                class="w-48 h-48 object-cover shrink-0"
                :alt="compareList[i - 1].name"
              />
              <div v-else class="w-56 h-56 bg-bg-base flex items-center justify-center shrink-0">
                <img
                  src="/icons/no-image.svg"
                  class="w-24 h-24"
                  alt=""
                >
              </div>
              <div class="flex-1 px-4 min-w-0">
                <nuxt-link :to="compareList[i - 1].url" class="type-headline-sm hover:underline block truncate">
                  {{ compareList[i - 1].name }}
                </nuxt-link>
                <div class="type-sm text-text-subtle truncate">Art.nr {{ compareList[i - 1].partNo }}</div>
              </div>
              <button
                :title="$t('compare.button.remove')"
                class="px-12 self-stretch flex items-center shrink-0"
                @click="removeFromCompare(compareList[i - 1].partNo)"
              >
                <img
                  src="/icons/close.svg"
                  class="w-12 h-12"
                  alt=""
                >
              </button>
            </div>

            <!-- empty spot -->
            <div
              v-else
              class="flex items-center gap-12 border border-dashed bg-bg-subtle border-border-base h-56 p-4"
            >
              <div class="w-48 h-48 flex items-center justify-center border border-dashed border-border-base text-border-base">
                {{ String(i).padStart(2, '0') }}
              </div>
              <span class="type-sm text-text-subtle uppercase">{{ $t('compare.sticky.empty-slot') }}</span>
            </div>
          </div>
        </div>

        <!-- Right: clear all + compare -->
        <div class="flex items-center gap-16 shrink-0">
          <button class="btn--text btn--md" @click="clearAll">
            {{ $t('compare.sticky.clear-all') }}
          </button>
          <nuxt-link
            :to="compareUrl"
            class="btn items-center gap-8"
            :class="{ disabled: compareList.length < 2}"
          >
            {{ $t('compare.sticky.button', { count: compareList.length }) }}
            <img
              src="/icons/arrow-right-inv.svg"
              class="w-16 h-16"
              alt=""
            >
          </nuxt-link>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import useCompare from '~/composables/useCompare';
import useLinkReplacer from '~/composables/useLinkReplacer';

const MAX_COMPARE = 3;

const route = useRoute();
const { compareList, removeFromCompare } = useCompare();
const { manualUrl } = useLinkReplacer();

const compareUrl = computed(() =>
  manualUrl(`/compare?partNo=${compareList.value.map(p => p.partNo).join(',')}`)
);

const clearAll = () => {
  compareList.value = [];
};
</script>
